import parseInlineStyle from 'style-to-object';
import type React from 'react';

/**
 * Converts the legacy iframe macro's inline CSS `style` string into a React style object.
 *
 * The legacy macro renders the iframe as raw HTML, so its `style` param is a CSS string
 * (e.g. `"width: 560px; height: 315px; border: 4px solid red;"`). React's `style` prop expects
 * an object (`{ width: '560px', ... }`), so the migrated macro has to parse the string.
 *
 * Tokenizing CSS correctly is deceptively hard — values legitimately contain `;` inside quotes,
 * `url(...)`, and `/* ... *‍/` comments — so rather than hand-roll a tokenizer we delegate that
 * to `style-to-object` (built on `inline-style-parser`, the same parser the remark/rehype and
 * `html-react-parser` ecosystems use). It returns raw kebab-case / vendor-prefixed property
 * names, which we then map to React style keys via {@link toReactStyleName}.
 */

// Legacy parity: strip declarations that could let embedded content escape its container or
// stack above the rest of the page. These are removed before parsing, not parsed and dropped.
const Z_INDEX_PATTERN = /z-index\s*:\s*-?\d+(?:\.\d+)?\s*(?:!\s*important)?\s*;?/gi;
const POSITION_PATTERN = /position\s*:\s*(absolute|fixed)\s*(?:!\s*important)?\s*;?/gi;

export const sanitiseStyle = (style?: string): string | undefined => {
	const sanitised = style?.replace(Z_INDEX_PATTERN, '').replace(POSITION_PATTERN, '').trim();

	return sanitised || undefined;
};

/**
 * Converts a CSS property name to its React style key (e.g. `border-radius` -> `borderRadius`,
 * `-ms-overflow-style` -> `msOverflowStyle`). CSS custom properties (`--foo`) are passed through
 * unchanged by the caller.
 */
const toReactStyleName = (name: string): string => {
	const camelCaseName = name
		.toLowerCase()
		.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());

	return camelCaseName.startsWith('Ms') ? `ms${camelCaseName.slice(2)}` : camelCaseName;
};

/**
 * Parses a legacy inline CSS string into a React style object, after stripping unsafe
 * declarations. Returns `undefined` when there is nothing safe and well-formed to render.
 *
 * `style-to-object` throws on a malformed declaration (e.g. a property with no `:`); we treat any
 * parse failure as "no usable style" rather than letting it crash the macro render (including
 * during SSR). Note this is all-or-nothing: a single malformed declaration discards the whole
 * style string rather than just that declaration.
 */
export const parseStyle = (style?: string): React.CSSProperties | undefined => {
	const sanitisedStyle = sanitiseStyle(style);

	if (!sanitisedStyle) {
		return undefined;
	}

	let parsedDeclarations: Record<string, string> | null;

	try {
		parsedDeclarations = parseInlineStyle(sanitisedStyle);
	} catch {
		return undefined;
	}

	if (!parsedDeclarations) {
		return undefined;
	}

	const styleEntries: Record<string, string> = {};

	for (const [propertyName, propertyValue] of Object.entries(parsedDeclarations)) {
		const reactPropertyName = propertyName.startsWith('--')
			? propertyName
			: toReactStyleName(propertyName);

		styleEntries[reactPropertyName] = propertyValue;
	}

	return Object.keys(styleEntries).length ? (styleEntries as React.CSSProperties) : undefined;
};
